import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    InlineStack,
    Layout,
    Page,
    SkeletonBodyText,
    SkeletonPage,
    Text,
    TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    createMachineRule,
    createOffering,
    ServiceBookingMachineRule,
    ServiceBookingOfferingSettings,
    ServiceBookingOperatingHour,
    ServiceBookingSettings,
} from '../../app/models/ServiceBooking';
import './ServiceBookingSettingsPage.css';

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toNumber = (value: string, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const ServiceBookingSettingsPage = () => {
    const navigate = useNavigate();
    const [settings, setSettings] = useState<ServiceBookingSettings>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setSettings(await agent.ServiceBookings.settings());
        } catch (requestError: any) {
            setError(requestError?.response?.data?.detail || requestError?.message || 'Booking settings could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const missingDepositVariants = useMemo(() =>
        settings?.offerings.filter((offering) =>
            offering.isActive && offering.depositAmount > 0 && !offering.depositShopifyVariantId) ?? [],
    [settings]);

    const patchSettings = (changes: Partial<ServiceBookingSettings>) => {
        setSaved(false);
        setSettings((current) => current ? { ...current, ...changes } : current);
    };

    const patchLocation = (changes: Partial<ServiceBookingSettings['location']>) => {
        setSaved(false);
        setSettings((current) => current ? { ...current, location: { ...current.location, ...changes } } : current);
    };

    const patchHours = (dayOfWeek: number, changes: Partial<ServiceBookingOperatingHour>) => {
        patchLocation({
            operatingHours: settings!.location.operatingHours.map((hours) =>
                hours.dayOfWeek === dayOfWeek ? { ...hours, ...changes } : hours),
        });
    };

    const patchOffering = (index: number, changes: Partial<ServiceBookingOfferingSettings>) => {
        patchSettings({
            offerings: settings!.offerings.map((offering, offeringIndex) =>
                offeringIndex === index ? { ...offering, ...changes } : offering),
        });
    };

    const removeOffering = (index: number) => {
        patchSettings({ offerings: settings!.offerings.filter((_, offeringIndex) => offeringIndex !== index) });
    };

    const patchRule = (index: number, changes: Partial<ServiceBookingMachineRule>) => {
        patchSettings({
            machineRules: settings!.machineRules.map((rule, ruleIndex) =>
                ruleIndex === index ? { ...rule, ...changes } : rule),
        });
    };

    const patchRuleCriterion = (index: number, key: string, value: string) => {
        const rule = settings!.machineRules[index];
        patchRule(index, { criteria: { ...rule.criteria, [key]: value } });
    };

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            const payload: ServiceBookingSettings = {
                ...settings,
                offerings: settings.offerings.map((offering, index) => ({ ...offering, displayOrder: index })),
                machineRules: settings.machineRules.map((rule, index) => ({ ...rule, priority: index })),
            };
            setSettings(await agent.ServiceBookings.updateSettings(payload));
            setSaved(true);
        } catch (requestError: any) {
            setError(
                requestError?.response?.data?.detail
                || requestError?.response?.data?.title
                || requestError?.message
                || 'Booking settings could not be saved.',
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading || !settings) {
        return (
            <SkeletonPage title="Booking settings">
                <Layout><Layout.Section><Card><SkeletonBodyText lines={12} /></Card></Layout.Section></Layout>
            </SkeletonPage>
        );
    }

    return (
        <Page
            title="Booking settings"
            subtitle="Single-location proof of concept"
            backAction={{ content: 'Service Booking', onAction: () => navigate('/service-booking') }}
            primaryAction={{ content: 'Save', onAction: save, loading: saving }}
        >
            <Layout>
                <Layout.Section>
                    <BlockStack gap="400">
                        <Banner title="POC configuration" tone="info">
                            <p>These settings support one location. The underlying booking model is ready to add more locations later.</p>
                        </Banner>
                        {error && <Banner title="Settings could not be saved" tone="critical"><p>{error}</p></Banner>}
                        {saved && <Banner title="Booking settings saved" tone="success"><p>The storefront configuration is up to date.</p></Banner>}
                        {settings.isEnabled && missingDepositVariants.length > 0 && (
                            <Banner title="Deposit products are incomplete" tone="warning">
                                <p>Each active paid service needs the numeric Shopify variant ID for its deposit product.</p>
                            </Banner>
                        )}

                        <Card>
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">Storefront availability</Text>
                                <Checkbox
                                    label="Enable the Service Booking storefront block"
                                    checked={settings.isEnabled}
                                    onChange={(isEnabled) => patchSettings({ isEnabled })}
                                />
                                <TextField
                                    label="Customer introduction"
                                    value={settings.customerIntroText}
                                    onChange={(customerIntroText) => patchSettings({ customerIntroText })}
                                    multiline={3}
                                    autoComplete="off"
                                />
                                <div className="pf-booking-settings-grid">
                                    <TextField
                                        label="Default machine category"
                                        helpText="Used when no machine rule matches."
                                        value={settings.defaultMachineCategoryKey}
                                        onChange={(defaultMachineCategoryKey) => patchSettings({ defaultMachineCategoryKey })}
                                        autoComplete="off"
                                    />
                                    <TextField
                                        label="Checkout hold (minutes)"
                                        helpText="Pending appointments hold capacity for this long."
                                        type="number"
                                        min={5}
                                        max={60}
                                        value={String(settings.pendingPaymentHoldMinutes)}
                                        onChange={(value) => patchSettings({ pendingPaymentHoldMinutes: toNumber(value, 15) })}
                                        autoComplete="off"
                                    />
                                </div>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="500">
                                <BlockStack gap="100">
                                    <Text as="h2" variant="headingMd">Location and availability</Text>
                                    <Text as="p" tone="subdued">The POC exposes the first active location to customers.</Text>
                                </BlockStack>
                                <div className="pf-booking-settings-grid">
                                    <TextField label="Location name" value={settings.location.name} onChange={(name) => patchLocation({ name })} autoComplete="organization" />
                                    <TextField label="Time zone" value={settings.location.timeZoneId} onChange={(timeZoneId) => patchLocation({ timeZoneId })} helpText="Use an IANA time zone such as America/Los_Angeles." autoComplete="off" />
                                    <div className="pf-booking-settings-wide">
                                        <TextField label="Address" value={settings.location.address} onChange={(address) => patchLocation({ address })} autoComplete="street-address" />
                                    </div>
                                    <TextField label="Slot interval (minutes)" type="number" min={5} max={120} value={String(settings.location.slotIntervalMinutes)} onChange={(value) => patchLocation({ slotIntervalMinutes: toNumber(value, 30) })} autoComplete="off" />
                                    <TextField label="Concurrent capacity" type="number" min={1} max={20} value={String(settings.location.capacity)} onChange={(value) => patchLocation({ capacity: toNumber(value, 1) })} helpText="Maximum overlapping appointments." autoComplete="off" />
                                </div>
                                <Divider />
                                <BlockStack gap="300">
                                    <Text as="h3" variant="headingSm">Weekly hours</Text>
                                    {settings.location.operatingHours
                                        .slice()
                                        .sort((left, right) => ((left.dayOfWeek || 7) - (right.dayOfWeek || 7)))
                                        .map((hours) => (
                                            <div className="pf-booking-hours-row" key={hours.dayOfWeek}>
                                                <Text as="span" fontWeight="semibold">{dayNames[hours.dayOfWeek]}</Text>
                                                <TextField label="Opens" labelHidden type="time" value={hours.opensAt} disabled={hours.isClosed} onChange={(opensAt) => patchHours(hours.dayOfWeek, { opensAt })} autoComplete="off" />
                                                <TextField label="Closes" labelHidden type="time" value={hours.closesAt} disabled={hours.isClosed} onChange={(closesAt) => patchHours(hours.dayOfWeek, { closesAt })} autoComplete="off" />
                                                <Checkbox label="Closed" checked={hours.isClosed} onChange={(isClosed) => patchHours(hours.dayOfWeek, { isClosed })} />
                                            </div>
                                        ))}
                                </BlockStack>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="500">
                                <InlineStack align="space-between" blockAlign="center">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Services and pricing</Text>
                                        <Text as="p" tone="subdued">An offering combines a service, machine category, duration, labor price, and deposit.</Text>
                                    </BlockStack>
                                    <Button onClick={() => patchSettings({ offerings: [...settings.offerings, createOffering(settings.offerings.length)] })}>Add service</Button>
                                </InlineStack>
                                {settings.offerings.map((offering, index) => (
                                    <div className="pf-booking-settings-row" key={`${offering.id}-${index}`}>
                                        <BlockStack gap="400">
                                            <InlineStack align="space-between" blockAlign="center">
                                                <Checkbox label="Active" checked={offering.isActive} onChange={(isActive) => patchOffering(index, { isActive })} />
                                                <Button tone="critical" variant="plain" onClick={() => removeOffering(index)}>Remove</Button>
                                            </InlineStack>
                                            <div className="pf-booking-settings-grid">
                                                <TextField label="Service name" value={offering.name} onChange={(name) => patchOffering(index, { name })} autoComplete="off" />
                                                <TextField label="Machine category" value={offering.machineCategoryKey} onChange={(machineCategoryKey) => patchOffering(index, { machineCategoryKey })} helpText="Use * to make this service available to every machine." autoComplete="off" />
                                                <div className="pf-booking-settings-wide">
                                                    <TextField label="Description" value={offering.description} onChange={(description) => patchOffering(index, { description })} autoComplete="off" />
                                                </div>
                                            </div>
                                            <div className="pf-booking-settings-grid pf-booking-settings-grid--four">
                                                <TextField label="Duration" suffix="min" type="number" min={5} value={String(offering.durationMinutes)} onChange={(value) => patchOffering(index, { durationMinutes: toNumber(value, 30) })} autoComplete="off" />
                                                <TextField label="Labor price" prefix="$" type="number" min={0} value={String(offering.laborPrice)} onChange={(value) => patchOffering(index, { laborPrice: toNumber(value) })} autoComplete="off" />
                                                <TextField label="Deposit" prefix="$" type="number" min={0} value={String(offering.depositAmount)} onChange={(value) => patchOffering(index, { depositAmount: toNumber(value) })} autoComplete="off" />
                                                <TextField label="Deposit variant ID" type="number" value={offering.depositShopifyVariantId ? String(offering.depositShopifyVariantId) : ''} onChange={(value) => patchOffering(index, { depositShopifyVariantId: value ? toNumber(value) : null })} helpText="Numeric Shopify variant ID; its Shopify price must equal the deposit." autoComplete="off" />
                                            </div>
                                        </BlockStack>
                                    </div>
                                ))}
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="500">
                                <InlineStack align="space-between" blockAlign="center">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Machine classification</Text>
                                        <Text as="p" tone="subdued">The first matching rule assigns the machine category used to select services and pricing.</Text>
                                    </BlockStack>
                                    <Button onClick={() => patchSettings({ machineRules: [...settings.machineRules, createMachineRule(settings.machineRules.length)] })}>Add rule</Button>
                                </InlineStack>
                                {settings.machineRules.map((rule, index) => (
                                    <div className="pf-booking-settings-row" key={`${rule.id}-${index}`}>
                                        <BlockStack gap="400">
                                            <InlineStack align="space-between" blockAlign="center">
                                                <Checkbox label="Active" checked={rule.isActive} onChange={(isActive) => patchRule(index, { isActive })} />
                                                <Button tone="critical" variant="plain" onClick={() => patchSettings({ machineRules: settings.machineRules.filter((_, ruleIndex) => ruleIndex !== index) })}>Remove</Button>
                                            </InlineStack>
                                            <div className="pf-booking-settings-grid">
                                                <TextField label="Rule name" value={rule.name} onChange={(name) => patchRule(index, { name })} autoComplete="off" />
                                                <TextField label="Resulting category" value={rule.machineCategoryKey} onChange={(machineCategoryKey) => patchRule(index, { machineCategoryKey })} autoComplete="off" />
                                            </div>
                                            <div className="pf-booking-settings-grid pf-booking-settings-grid--four">
                                                {['year', 'make', 'model', 'submodel'].map((key) => (
                                                    <TextField
                                                        key={key}
                                                        label={key === 'submodel' ? 'Submodel / trim' : key.charAt(0).toUpperCase() + key.slice(1)}
                                                        value={rule.criteria[key] || ''}
                                                        onChange={(value) => patchRuleCriterion(index, key, value)}
                                                        helpText="Blank matches any value."
                                                        autoComplete="off"
                                                    />
                                                ))}
                                            </div>
                                        </BlockStack>
                                    </div>
                                ))}
                            </BlockStack>
                        </Card>

                        <Box paddingBlockEnd="600">
                            <InlineStack align="end" gap="300">
                                <Button onClick={() => navigate('/service-booking')}>Cancel</Button>
                                <Button variant="primary" onClick={save} loading={saving}>Save booking settings</Button>
                            </InlineStack>
                        </Box>
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ServiceBookingSettingsPage;
